import { useCallback, useEffect, useRef, useState } from 'react'
import { EditorContent, Extension, InputRule, Node, useEditor } from '@tiptap/react'
import StarterKit from '@tiptap/starter-kit'
import TaskList from '@tiptap/extension-task-list'
import TaskItem from '@tiptap/extension-task-item'
import Placeholder from '@tiptap/extension-placeholder'
import Image from '@tiptap/extension-image'
import Highlight from '@tiptap/extension-highlight'
import { TableKit } from '@tiptap/extension-table'
import { BulletList } from '@tiptap/extension-list'
import { CLIENT_ID, hasPendingEdit, useStore } from '../store'
import type { Doc } from '../types'
import { editorDate } from '../lib/format'
import { ACCEPTED_FILE_TYPES, UploadError, imageFilesFrom, uploadFile, uploadImage } from '../lib/upload'
import { formatBytes } from '../lib/compress'
import { evaluate } from '../lib/calc'
import {
  AaIcon, BoldIcon, ChecklistIcon, ChevronLeft, CodeIcon, ComposeIcon, HighlightIcon, ImageIcon,
  ItalicIcon, LinkIcon, ListBulletIcon, ListNumberIcon, QuoteIcon, SearchIcon, SpinnerIcon,
  StrikeIcon, TableIcon, UnderlineIcon,
  HistoryIcon,
} from './icons'
import { Popover } from './Popover'

/**
 * A file attachment — a PDF today — as a self-contained block.
 *
 * Only the URL and a little metadata are stored; the bytes live in Storage
 * exactly as images do.
 */
const FileAttachment = Node.create({
  name: 'fileAttachment',
  group: 'block',
  atom: true,
  draggable: true,
  addAttributes() {
    return {
      href: { default: '' },
      name: { default: 'Attachment' },
      size: { default: 0 },
      mime: { default: 'application/pdf' },
    }
  },
  parseHTML() { return [{ tag: 'a[data-file-attachment]' }] },
  renderHTML({ node }) {
    const kb = Number(node.attrs.size) || 0
    const pretty = kb < 1024 * 1024 ? `${Math.round(kb / 1024)} KB` : `${(kb / 1048576).toFixed(1)} MB`
    return ['a', {
      'data-file-attachment': '', class: 'file-attachment',
      href: node.attrs.href, target: '_blank', rel: 'noreferrer',
      'data-name': node.attrs.name, 'data-size': String(node.attrs.size), 'data-mime': node.attrs.mime,
    }, ['span', { class: 'file-icon' }, 'PDF'],
       ['span', { class: 'file-meta' },
         ['span', { class: 'file-name' }, node.attrs.name],
         ['span', { class: 'file-size' }, pretty]]]
  },
  renderText({ node }) { return String(node.attrs.name ?? '') },
})

/**
 * The computed answer, as its own inline node.
 *
 * It is an atom, so the caret cannot land inside it and you cannot half-edit a
 * result into nonsense — you either keep it or delete it whole. That is what
 * the distinct colour and face are signalling, so the two must agree.
 */
const MathResult = Node.create({
  name: 'mathResult',
  inline: true,
  group: 'inline',
  atom: true,
  selectable: true,
  addAttributes() {
    return { value: { default: '', parseHTML: (el: HTMLElement) => el.getAttribute('data-value') ?? el.textContent } }
  },
  parseHTML() { return [{ tag: 'span[data-math-result]' }] },
  renderHTML({ node }) {
    // Each digit is its own span so it can fade in a beat after the one before.
    const chars = String(node.attrs.value ?? '').split('')
    return [
      'span',
      { 'data-math-result': '', 'data-value': node.attrs.value, class: 'math-result' },
      ...chars.map((c, i) => [
        'span',
        { class: 'math-digit', style: `animation-delay:${i * 55}ms` },
        c === ' ' ? '\u00a0' : c,
      ]),
    ]
  },
  // Keeps the answer in plain-text extraction, so search and previews see it.
  renderText({ node }) { return String(node.attrs.value ?? '') },
})

/**
 * Types the answer when you finish a sum with "=", the way Math Notes does.
 *
 * The expression is parsed by lib/calc, never eval'd — note bodies are user
 * content that also arrives from other devices.
 */
const MathRule = Extension.create({
  name: 'mathRule',
  addInputRules() {
    return [
      new InputRule({
        // Only characters that can appear in a sum, ending at the "=" just typed.
        find: /(^|[\s(])([-+*/^%×÷().,\d\s]*[-+*/^×÷][-+*/^%×÷().,\d\s]*)=$/,
        handler: ({ state, range, match, chain }) => {
          const answer = evaluate(match[2])
          if (answer === null) return null          // leave prose untouched
          const lead = match[1] ?? ''
          chain().insertContentAt({ from: range.from, to: range.to }, [
            { type: 'text', text: `${lead}${match[2].trimEnd()} = ` },
            { type: 'mathResult', attrs: { value: answer } },
          ]).run()
          void state
          return undefined
        },
      }),
    ]
  },
})

/**
 * Tab belongs to the editor, not to the browser's focus order — without this it
 * moves focus to the toolbar's first button instead of indenting.
 */
const TabKey = Extension.create({
  name: 'tabKey',
  // Above StarterKit (100), whose Enter would otherwise claim the key first and
  // split the selection into blocks instead of clearing the note.
  priority: 1000,
  addKeyboardShortcuts() {
    const listType = () =>
      this.editor.isActive('taskItem') ? 'taskItem'
        : this.editor.isActive('listItem') ? 'listItem' : null

    return {
      Tab: () => {
        const type = listType()
        if (type) return this.editor.commands.sinkListItem(type)
        return this.editor.commands.insertContent('\t')
      },
      // Select-all then Return empties the note and leaves one line to type on,
      // the way any other editor behaves. ProseMirror's default splits the
      // selected blocks instead, leaving debris behind.
      Enter: () => {
        const { state } = this.editor
        const { from, to, empty } = state.selection
        if (empty) return false
        const coversAll = from <= 1 && to >= state.doc.content.size - 1
        if (!coversAll) return false
        this.editor
          .chain()
          .setContent({ type: 'doc', content: [{ type: 'heading', attrs: { level: 1 } }] },
                      { emitUpdate: true })
          .focus('end')
          .run()
        // Must claim the key: setContent's own return value is falsy, and
        // falling through lets StarterKit's Enter split the fresh block in two.
        return true
      },
      'Shift-Tab': () => {
        const type = listType()
        if (type) return this.editor.commands.liftListItem(type)
        return true // swallow it so focus stays in the note
      },
    }
  },
})

/** Apple Notes has both bulleted and dashed lists; ProseMirror has only one. */
const DashedBulletList = BulletList.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      dashed: {
        default: false,
        parseHTML: (el: HTMLElement) => el.getAttribute('data-dashed') === 'true',
        renderHTML: (attrs: Record<string, unknown>) => (attrs.dashed ? { 'data-dashed': 'true' } : {}),
      },
    }
  },
})

const STYLES = [
  { label: 'Title', size: 21, weight: 700, apply: (c: any) => c.setHeading({ level: 1 }), is: 'heading1' },
  { label: 'Heading', size: 17, weight: 700, apply: (c: any) => c.setHeading({ level: 2 }), is: 'heading2' },
  { label: 'Subheading', size: 15, weight: 600, apply: (c: any) => c.setHeading({ level: 3 }), is: 'heading3' },
  { label: 'Body', size: 14, weight: 400, apply: (c: any) => c.setParagraph(), is: 'paragraph' },
  { label: 'Monospaced', size: 13, weight: 400, mono: true, apply: (c: any) => c.setCodeBlock(), is: 'codeBlock' },
  { label: 'Block Quote', size: 14, weight: 400, quote: true, apply: (c: any) => c.toggleBlockquote(), is: 'blockquote' },
] as const

export function Editor({ onBack }: { onBack: () => void }) {
  const store = useStore()
  const note = store.notes.find((n) => n.id === store.selectedId) ?? null
  const inTrash = note?.trashedAt != null
  const [showStyles, setShowStyles] = useState(false)
  const [showTable, setShowTable] = useState(false)
  // The panels are portalled out of the toolbar, so they need their anchor.
  const stylesBtn = useRef<HTMLDivElement>(null)
  const tableBtn = useRef<HTMLDivElement>(null)
  const [uploading, setUploading] = useState(0)
  const [saved, setSaved] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const loadedId = useRef<string | null>(null)
  const fileInput = useRef<HTMLInputElement>(null)
  const insertRef = useRef<((files: File[]) => void) | null>(null)

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: { levels: [1, 2, 3] },
        bulletList: false,
        link: { openOnClick: false, autolink: true },
      }),
      DashedBulletList,
      TaskList,
      TaskItem.configure({ nested: true }),
      Placeholder.configure({ placeholder: 'Start writing…' }),
      Image.configure({ allowBase64: false }),
      Highlight,
      TableKit.configure({ table: { resizable: true } }),
      TabKey,
      MathResult,
      FileAttachment,
      MathRule,
    ],
    editorProps: {
      attributes: { spellcheck: 'true' },
      handleDrop: (_view, event) => {
        const files = imageFilesFrom((event as DragEvent).dataTransfer)
        if (!files.length) return false
        event.preventDefault()
        insertRef.current?.(files)
        return true
      },
      handlePaste: (_view, event) => {
        const files = imageFilesFrom((event as ClipboardEvent).clipboardData)
        if (!files.length) return false
        event.preventDefault()
        insertRef.current?.(files)
        return true
      },
    },
    onUpdate: ({ editor }) => {
      const id = loadedId.current
      if (!id || inTrash) return
      store.saveNote(id, editor.getJSON() as Doc)
    },
  })

  const insertImages = useCallback(async (files: File[]) => {
    if (!note || inTrash || !editor) return
    setError(null)
    for (const file of files) {
      setUploading((u) => u + 1)
      try {
        if (ACCEPTED_FILE_TYPES.includes(file.type)) {
          const doc = await uploadFile(file, note.id)
          editor.chain().focus().insertContent({
            type: 'fileAttachment',
            attrs: { href: doc.url, name: doc.name, size: doc.size, mime: doc.mime },
          }).run()
          continue
        }
        const up = await uploadImage(file, note.id)
        editor.chain().focus().setImage({ src: up.url, alt: file.name }).run()
        if (up.compressed) {
          setSaved(`Compressed ${formatBytes(up.originalBytes)} to ${formatBytes(up.bytes)}`)
          setTimeout(() => setSaved(null), 4000)
        }
      } catch (e) {
        setError(e instanceof UploadError ? e.message : (e as Error).message)
      } finally {
        setUploading((u) => u - 1)
      }
    }
  }, [note, inTrash, editor])

  // editorProps captures its handlers once, so reach the latest closure by ref.
  useEffect(() => { insertRef.current = (files) => void insertImages(files) }, [insertImages])

  useEffect(() => {
    if (!editor || !note) return
    if (loadedId.current === note.id) return
    loadedId.current = note.id
    editor.commands.setContent(note.content as never, { emitUpdate: false })
  }, [editor, note, inTrash])

  /**
   * Take focus only when something explicitly asked for it — a new note, a
   * duplicate, or Return/Tab from the list. Merely selecting a note leaves
   * focus in the list so the arrow keys keep walking it, as in Notes.
   */
  useEffect(() => {
    if (!editor || !note || inTrash) return
    if (store.focusNoteId !== note.id) return
    store.requestEditorFocus(null)
    setTimeout(() => editor.commands.focus('end'), 0)
  }, [editor, note, inTrash, store])

  /** Apply edits that arrived from another device into the open note. */
  useEffect(() => {
    if (!editor || !note) return
    if (loadedId.current !== note.id) return
    if (note.clientId === CLIENT_ID) return
    if (hasPendingEdit(note.id)) return
    const { from } = editor.state.selection
    editor.commands.setContent(note.content as never, { emitUpdate: false })
    const max = Math.max(0, editor.state.doc.content.size - 1)
    editor.commands.setTextSelection(Math.min(from, max))
  }, [editor, note, note?.updatedAt, note?.clientId])

  useEffect(() => { editor?.setEditable(!inTrash) }, [editor, inTrash])

  // Dev-only handle, so the editor can be driven from the console.
  useEffect(() => {
    if (import.meta.env.DEV) (window as unknown as Record<string, unknown>).__editor = editor
  }, [editor])
  useEffect(() => { if (!store.selectedId) loadedId.current = null }, [store.selectedId])

  useEffect(() => {
    const flush = () => store.flushPending()
    window.addEventListener('beforeunload', flush)
    document.addEventListener('visibilitychange', flush)
    return () => {
      window.removeEventListener('beforeunload', flush)
      document.removeEventListener('visibilitychange', flush)
    }
  }, [store])

  const setLink = useCallback(() => {
    if (!editor) return
    const prev = editor.getAttributes('link').href as string | undefined
    const url = window.prompt('Link URL', prev ?? 'https://')
    if (url === null) return
    if (!url.trim()) { editor.chain().focus().unsetLink().run(); return }
    editor.chain().focus().extendMarkRange('link').setLink({ href: url.trim() }).run()
  }, [editor])

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k' && editor?.isFocused) {
        e.preventDefault(); setLink()
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [editor, setLink])

  // More than one note highlighted: there is no single note to edit, so say
  // what is selected rather than showing one of them and implying the rest
  // are not there.
  const multi = store.selectedIds.length > 1
  if (!note || multi) {
    return (
      <section className="pane-editor">
        <div className="toolbar">
          <button className="back-btn" onClick={onBack}><ChevronLeft size={15} />Notes</button>
        </div>
        <div className="empty-state">
          {multi ? `${store.selectedIds.length} Notes Selected` : 'No Notes Selected'}
        </div>
      </section>
    )
  }

  const btn = (active: boolean, title: string, onClick: () => void, kids: React.ReactNode) => (
    <button className={`tb-btn ${active ? 'active' : ''}`} title={title} disabled={inTrash} onClick={onClick}>
      {kids}
    </button>
  )

  const pbtn = (active: boolean, title: string, onClick: () => void, kids: React.ReactNode) => (
    <button className={`panel-btn ${active ? 'active' : ''}`} title={title} onClick={onClick}>{kids}</button>
  )

  const toggleDashed = () => {
    if (!editor) return
    const c = editor.chain().focus()
    if (editor.isActive('bulletList', { dashed: true })) c.toggleBulletList().run()
    else if (editor.isActive('bulletList')) c.updateAttributes('bulletList', { dashed: true }).run()
    else c.toggleBulletList().updateAttributes('bulletList', { dashed: true }).run()
  }

  return (
    <section className="pane-editor">
      <div className="toolbar toolbar-editor">
        <button className="back-btn" onClick={onBack}><ChevronLeft size={16} />Notes</button>

        <button className="tb-btn" title="New Note (⌘N)" disabled={inTrash}
                onClick={() => store.newNote()}><ComposeIcon /></button>

        <div ref={stylesBtn} style={{ position: 'relative' }}>
          {btn(showStyles, 'Format', () => setShowStyles((v) => !v), <AaIcon />)}
          {showStyles && editor && (
            <Popover anchor={stylesBtn.current} className="format-panel"
                     onClose={() => setShowStyles(false)}>
              <div>
                {STYLES.map((st) => {
                  const active = st.is.startsWith('heading')
                    ? editor.isActive('heading', { level: Number(st.is.slice(-1)) })
                    : editor.isActive(st.is)
                  return (
                    <button
                      key={st.label}
                      className={`popover-row ${active ? 'active' : ''}`}
                      onClick={() => { st.apply(editor.chain().focus()).run() }}
                      style={{
                        fontSize: st.size,
                        fontWeight: st.weight,
                        fontFamily: 'mono' in st && st.mono ? 'var(--mono)' : 'inherit',
                        fontStyle: 'quote' in st && st.quote ? 'italic' : 'normal',
                      }}
                    >
                      {st.label}
                    </button>
                  )
                })}

                <div className="panel-sep" />
                {/* Apple keeps B/I/U/S inside this panel, not on the toolbar. */}
                <div className="panel-row">
                  {pbtn(!!editor.isActive('bold'), 'Bold (⌘B)', () => editor.chain().focus().toggleBold().run(), <BoldIcon size={17} />)}
                  {pbtn(!!editor.isActive('italic'), 'Italic (⌘I)', () => editor.chain().focus().toggleItalic().run(), <ItalicIcon size={17} />)}
                  {pbtn(!!editor.isActive('underline'), 'Underline (⌘U)', () => editor.chain().focus().toggleUnderline().run(), <UnderlineIcon size={17} />)}
                  {pbtn(!!editor.isActive('strike'), 'Strikethrough', () => editor.chain().focus().toggleStrike().run(), <StrikeIcon size={17} />)}
                  {pbtn(!!editor.isActive('highlight'), 'Highlight', () => editor.chain().focus().toggleHighlight().run(), <HighlightIcon size={17} />)}
                  {pbtn(!!editor.isActive('code'), 'Inline Code', () => editor.chain().focus().toggleCode().run(), <CodeIcon size={17} />)}
                </div>

                <div className="panel-sep" />
                <div className="panel-row">
                  {pbtn(!!editor.isActive('bulletList', { dashed: false }), 'Bulleted List', () => editor.chain().focus().toggleBulletList().run(), <ListBulletIcon size={17} />)}
                  {pbtn(!!editor.isActive('bulletList', { dashed: true }), 'Dashed List', toggleDashed, <QuoteIcon size={17} />)}
                  {pbtn(!!editor.isActive('orderedList'), 'Numbered List', () => editor.chain().focus().toggleOrderedList().run(), <ListNumberIcon size={17} />)}
                </div>
              </div>
            </Popover>
          )}
        </div>

        {btn(!!editor?.isActive('taskList'), 'Checklist', () => editor?.chain().focus().toggleTaskList().run(), <ChecklistIcon />)}

        <div style={{ position: 'relative' }}>
          {btn(showTable || !!editor?.isActive('table'), 'Table', () => setShowTable((v) => !v), <TableIcon />)}
          {showTable && editor && (
            <Popover anchor={tableBtn.current} onClose={() => setShowTable(false)}>
              <div style={{ minWidth: 190 }}>
                {([
                  ['Insert Table', () => editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run()],
                  ['Add Row Above', () => editor.chain().focus().addRowBefore().run()],
                  ['Add Row Below', () => editor.chain().focus().addRowAfter().run()],
                  ['Add Column Left', () => editor.chain().focus().addColumnBefore().run()],
                  ['Add Column Right', () => editor.chain().focus().addColumnAfter().run()],
                  ['Delete Row', () => editor.chain().focus().deleteRow().run()],
                  ['Delete Column', () => editor.chain().focus().deleteColumn().run()],
                  ['Delete Table', () => editor.chain().focus().deleteTable().run()],
                ] as Array<[string, () => void]>).map(([label, run]) => (
                  <button key={label} className="popover-row" style={{ fontSize: 13 }}
                          onClick={() => { run(); setShowTable(false) }}>{label}</button>
                ))}
              </div>
            </Popover>
          )}
        </div>

        {btn(false, 'Insert Image or PDF', () => fileInput.current?.click(), uploading ? <SpinnerIcon /> : <ImageIcon />)}
        {btn(!!editor?.isActive('link'), 'Add Link (⌘K)', setLink, <LinkIcon />)}

        <div className="tb-spacer" />
        <button
          className={`tb-btn ${store.historyFor === note.id ? 'active' : ''}`}
          title="Version History"
          onClick={() => (store.historyFor === note.id ? store.closeHistory() : store.openHistory(note.id))}
        >
          <HistoryIcon />
        </button>
        <button className="tb-btn" title="Search (⌘F)"
                onClick={() => document.querySelector<HTMLInputElement>('.search input')?.focus()}>
          <SearchIcon />
        </button>
      </div>

      <input
        ref={fileInput} type="file" accept="image/*,application/pdf" multiple hidden
        onChange={(e) => {
          const files = [...(e.target.files ?? [])]
          e.target.value = ''
          if (files.length) void insertImages(files)
        }}
      />

      {inTrash && (
        <div className="trash-banner">
          <span>This note will be deleted permanently.</span>
          <button onClick={() => store.restoreNote(note.id)}>Recover</button>
          <button style={{ color: 'var(--danger)' }} onClick={() => store.deleteForever(note.id)}>Delete Now</button>
        </div>
      )}

      {error && (
        <div className="trash-banner" style={{ color: 'var(--danger)' }}>
          <span>{error}</span>
          <button onClick={() => setError(null)}>Dismiss</button>
        </div>
      )}
      {uploading > 0 && (
        <div className="trash-banner">
          <span>Uploading {uploading} image{uploading > 1 ? 's' : ''}…</span>
        </div>
      )}
      {saved && !uploading && (
        <div className="trash-banner"><span>{saved}</span></div>
      )}

      <div
        className="editor-scroll"
        onMouseDown={(e) => {
          // ProseMirror only occupies as much height as its content, so a click
          // in the space below the last block never reaches it and nothing
          // happens. Treat it as "put the caret at the end", like Notes does.
          if (inTrash || !editor) return
          const target = e.target as HTMLElement
          if (target.closest('.ProseMirror')) return
          e.preventDefault()
          editor.commands.focus('end')
        }}
      >
        <div className="editor-inner">
          <div className="editor-date">{editorDate(note.updatedAt)}</div>
          <EditorContent editor={editor} />
        </div>
      </div>
    </section>
  )
}
